/**
 * Pure decisions behind the audio engine (Phase 8): which variant of a cue plays, which voice
 * gives way when the pool or a cue's instance cap is full, and the gains. Tested in Vitest;
 * the engine only wires the results to Web Audio nodes.
 */

export interface ActiveVoice {
  cueId: string;
  priority: number;
  startedAt: number;
}

/** A variant index, never the same as `previous` when the cue has more than one file. */
export function pickVariant(count: number, previous: number, random: () => number): number {
  if (count <= 1) return 0;
  if (previous < 0 || previous >= count) return Math.min(count - 1, Math.floor(random() * count));
  const r = Math.min(count - 2, Math.floor(random() * (count - 1)));
  return r >= previous ? r + 1 : r;
}

export type SlotChoice<T> = { kind: 'free' } | { kind: 'steal'; victim: T } | { kind: 'refuse' };

/**
 * Where a new voice goes: a free slot; the oldest voice of the same cue when that cue is at its
 * instance cap; the lowest-priority, oldest voice of no higher priority when the pool is full;
 * or nowhere, when everything playing outranks the newcomer.
 */
export function chooseSlot<T extends ActiveVoice>(
  active: readonly T[],
  capacity: number,
  cueId: string,
  maxInstances: number,
  priority: number,
): SlotChoice<T> {
  const same = active.filter((v) => v.cueId === cueId);
  if (same.length >= maxInstances) {
    const victim = oldest(same);
    return victim ? { kind: 'steal', victim } : { kind: 'refuse' };
  }
  if (active.length < capacity) return { kind: 'free' };
  const candidates = active.filter((v) => v.priority <= priority);
  if (candidates.length === 0) return { kind: 'refuse' };
  const lowest = Math.min(...candidates.map((v) => v.priority));
  const victim = oldest(candidates.filter((v) => v.priority === lowest));
  return victim ? { kind: 'steal', victim } : { kind: 'refuse' };
}

function oldest<T extends ActiveVoice>(voices: readonly T[]): T | null {
  let best: T | null = null;
  for (const v of voices) if (best === null || v.startedAt < best.startedAt) best = v;
  return best;
}

export function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

/** A slider level (0..1) as a gain: squared, so the middle of the slider sounds like half. */
export function levelGain(level: number): number {
  const l = Math.min(1, Math.max(0, level));
  return l * l;
}

/** The playback rate for a cue: its pitch spread by up to `variance` either way. */
export function playbackRate(pitch: number, variance: number, random: () => number): number {
  return pitch * (1 + (random() * 2 - 1) * variance);
}

/** True when the cue may sound again: its retrigger gap has passed since it last did. */
export function mayRetrigger(lastAt: number | undefined, now: number, gap: number): boolean {
  return gap <= 0 || lastAt === undefined || now - lastAt >= gap;
}
